import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, LogOut, Loader2, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminCheck, adminCBGetSettings, adminCBUpdateSettings, adminLogout } from "../lib/api";
import { AdminNav } from "./AdminContinueBuilding";

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export default function AdminSettings() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [price, setPrice] = useState(149);
  const [phases, setPhases] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminCheck()
      .then((r) => {
        if (!r.admin) { setAuthorized(false); navigate("/"); return; }
        setAuthorized(true);
        adminCBGetSettings().then((s) => {
          setPrice(s.monthly_price);
          setPhases((s.default_phases || []).map((p) => ({ name: p.name || "", description: p.description || "", includes: toLines(p.includes) })));
        }).catch(() => {});
      })
      .catch(() => { setAuthorized(false); navigate("/"); });
  }, [navigate]);

  const save = async () => {
    setSaving(true);
    try {
      await adminCBUpdateSettings({
        monthly_price: Number(price),
        default_phases: phases.map((p) => ({ name: p.name, description: p.description, includes: fromLines(p.includes) })),
      });
      toast.success("Settings saved");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  const setPhase = (i, k, v) => setPhases((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const addPhase = () => setPhases((ps) => [...ps, { name: "New Phase", description: "", includes: "" }]);
  const removePhase = (i) => setPhases((ps) => ps.filter((_, idx) => idx !== i));

  if (authorized === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]"><Loader2 className="animate-spin text-[#D4AF37]" size={30} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-settings">
      <header className="border-b border-white/10 bg-[#0B0B0B]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Rocket className="text-[#D4AF37]" size={18} /></div>
              <div><p className="font-display font-bold leading-tight">Command Center</p><p className="text-xs text-[#71717A]">Settings</p></div>
            </div>
            <button onClick={async () => { await adminLogout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="admin-logout-btn">
              <LogOut size={16} /> Sign out
            </button>
          </div>
          <AdminNav active="settings" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-10 space-y-8">
        <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
          <h2 className="font-display font-semibold text-lg mb-4">Continue Building Program</h2>
          <div className="max-w-xs">
            <label className="block text-xs text-[#71717A] mb-1.5">Monthly subscription price ($)</label>
            <input type="number" min="1" className="admin-input" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="settings-price" />
            <p className="text-xs text-[#71717A] mt-2">New subscriptions are billed at this price for the first month.</p>
          </div>
        </section>

        <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-semibold text-lg">Default Build Phases</h2>
              <p className="text-xs text-[#71717A] mt-0.5">Template used when a new customer subscribes. ({phases.length} phases)</p>
            </div>
            <button onClick={addPhase} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5 transition-colors" data-testid="settings-add-phase"><Plus size={15} /> Add</button>
          </div>
          <div className="space-y-4">
            {phases.map((p, i) => (
              <div key={i} className="border border-white/10 rounded-xl p-4" data-testid={`settings-phase-${i + 1}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-[#D4AF37]">Phase {i + 1}</span>
                  <button onClick={() => removePhase(i)} className="text-red-400 hover:text-red-300"><Trash2 size={15} /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className="admin-input" value={p.name} onChange={(e) => setPhase(i, "name", e.target.value)} placeholder="Phase name" data-testid={`settings-phase-name-${i + 1}`} />
                  <input className="admin-input" value={p.description} onChange={(e) => setPhase(i, "description", e.target.value)} placeholder="Short description" />
                </div>
                <textarea rows={3} className="admin-input resize-none mt-3" value={p.includes} onChange={(e) => setPhase(i, "includes", e.target.value)} placeholder="Includes (one per line)" />
              </div>
            ))}
          </div>
        </section>

        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-50" data-testid="settings-save-btn">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save settings
        </button>
      </main>
    </div>
  );
}
